import { z } from 'zod';

import { ProductKeySchema } from '../../billing/product-key/product-key';
import { UuidSchema } from '../../common/id/id';
import { DiscountSourceSchema } from '../discount-source/discount-source';
import { DiscountAmountSchema, DiscountTypeSchema } from '../discount-type/discount-type';
import {
  DISCOUNT_RULE_MAX_PRODUCTS,
  DISCOUNT_RULE_NAME_MAX_LENGTH,
  DiscountMaxPerUserSchema,
  DiscountValidityDaysSchema,
} from '../discount-rule/discount-rule';
import { StackingPolicySchema } from '../stacking-policy/stacking-policy';

/**
 * The editable half of a discount rule.
 *
 * `discountType` and `value` are validated **together** by the service through
 * `isValidDiscountAmount`, not by a `.refine` here: an object refinement cannot
 * put its message on `value`, and the update schema's `.partial()` would make it
 * re-check a pair it may only have half of (P16b).
 *
 * `stackingPolicy` is optional on create and defaults from
 * `courses.discountStackingDefault`, resolved by the service — a coded default
 * in this schema would be a second place the policy is decided, and the two
 * would disagree the day an operator changed the setting.
 */
export const DiscountRuleWritableShape = {
  name: z.string().trim().min(1).max(DISCOUNT_RULE_NAME_MAX_LENGTH),
  discountType: DiscountTypeSchema,
  value: DiscountAmountSchema,
  appliesToProductKeys: z.array(ProductKeySchema).max(DISCOUNT_RULE_MAX_PRODUCTS),
  validityDays: DiscountValidityDaysSchema,
  stackingPolicy: StackingPolicySchema.optional(),
  maxPerUser: DiscountMaxPerUserSchema,
  isActive: z.boolean(),
};

/**
 * Creating a rule (`courses.manage`).
 *
 * `source` and `sourceId` are settable here and nowhere else: together they are
 * what the issuance listener matches on, and re-pointing a rule after grants
 * have been issued would leave those grants describing a reason that no longer
 * exists. Editing the *value* is fine and matrix row 18 says why — grants
 * snapshot their value at issuance, so an edit reaches future events only.
 */
export const DiscountRuleCreateSchema = z
  .object({
    source: DiscountSourceSchema,
    sourceId: UuidSchema,
    ...DiscountRuleWritableShape,
  })
  .strict();

export type DiscountRuleCreateRequest = z.infer<typeof DiscountRuleCreateSchema>;
